import { type ReactNode } from 'react'
import { ShieldAlert } from 'lucide-react'
import { useUser, useCan } from '../hooks/useUser'

/**
 * Route-level RBAC guard. Renders `children` only when the user holds `perm`
 * (admins always pass via useCan's master override). Otherwise it shows a clean
 * "no access" panel instead of letting the page mount and 403 on its data
 * fetches.
 *
 * The backend require_permission gate remains the true security boundary — this
 * only keeps the UI honest about which screens a user can actually open, so a
 * non-admin who holds one permission and manually types another admin URL gets
 * a clear message rather than a broken, half-empty page.
 *
 * Pass `perm="*"` for aggregate dashboards (like the admin Overview) that pull
 * from many areas at once: any admin permission — or legacy is_admin — grants
 * access, and the page's own fetches degrade gracefully for the areas the user
 * cannot read.
 */
export function RequirePermission({ perm, children }: { perm: string; children: ReactNode }) {
  const user = useUser()
  const can = useCan()
  const allowed = perm === '*' ? user.isAdmin || user.permissions.length > 0 : can(perm)
  if (allowed) return <>{children}</>
  return <NoAccess />
}

function NoAccess() {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center mb-4">
        <ShieldAlert className="w-6 h-6 text-amber-400" />
      </div>
      <h1 className="text-xl font-semibold mb-2">You don&apos;t have access to this page</h1>
      <p className="text-muted-foreground max-w-md">
        This screen needs a permission your account hasn&apos;t been granted. If you need
        access, ask a platform admin to update your role.
      </p>
    </div>
  )
}
